import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  useTheme,
  Menu,
  Divider,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreIcon,
  Gavel as GavelIcon,
  TrendingUp as TrendingUpIcon,
  AccessTime as PendingIcon,
  CheckCircle as ResolvedIcon,
  Block as DismissedIcon
} from '@mui/icons-material';
import {
  getAllDisputes,
  getDisputesByStatus
} from '../../../services/dispute';
import {
  DISPUTE_STATUS,
  getDisputeStatusConfig,
  DISPUTE_TYPE_LABELS,
  getDisputeReasonLabel,
  formatDisputeNumber,
  isDisputeResolved
} from '../../../constants/disputeConstants';
import DisputeReviewModal from './DisputeReviewModal';

const AdminDisputeDashboard = () => {
  const theme = useTheme();
  
  // State
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [disputeTypeFilter, setDisputeTypeFilter] = useState('ALL');
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    underReview: 0,
    escalated: 0,
    resolved: 0,
    dismissed: 0
  });
  
  // Modal & menu state
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuDispute, setMenuDispute] = useState(null);

  useEffect(() => {
    fetchDisputes();
  }, [page, rowsPerPage, statusFilter]);

  const fetchDisputes = async () => {
    setLoading(true);
    setError('');

    try {
      let response;
      if (statusFilter === 'ALL') {
        response = await getAllDisputes(page, rowsPerPage);
      } else {
        response = await getDisputesByStatus(statusFilter, page, rowsPerPage);
      }
      
      setDisputes(response.content);
      setTotalElements(response.page.totalElements);
      
      // Calculate stats
      calculateStats(response.content);
    } catch (err) {
      console.error('Error fetching disputes:', err);
      setError('Failed to load disputes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (disputeList) => {
    const newStats = {
      total: disputeList.length,
      open: disputeList.filter(d => d.status === 'OPEN').length,
      underReview: disputeList.filter(d => d.status === 'UNDER_REVIEW').length,
      escalated: disputeList.filter(d => d.status === 'ESCALATED').length,
      resolved: disputeList.filter(d => 
        ['RESOLVED_FOR_FILER', 'RESOLVED_FOR_RESPONDENT', 'RESOLVED_MUTUAL'].includes(d.status)
      ).length,
      dismissed: disputeList.filter(d => d.status === 'DISMISSED').length
    };
    setStats(newStats);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchDisputes();
  };

  const handleMenuOpen = (event, dispute) => {
    setMenuAnchor(event.currentTarget);
    setMenuDispute(dispute);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuDispute(null);
  };

  const handleReviewDispute = (dispute) => {
    setSelectedDispute(dispute);
    setReviewModalOpen(true);
    handleMenuClose();
  };

  const handleReviewSuccess = () => {
    setReviewModalOpen(false);
    setSelectedDispute(null);
    fetchDisputes();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFilteredDisputes = () => {
    let filtered = disputes;

    // Search filter
    if (searchQuery) {
      filtered = filtered.filter(dispute =>
        dispute.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispute.disputeNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispute.filerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        dispute.respondentName?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Service type filter
    if (disputeTypeFilter !== 'ALL') {
      filtered = filtered.filter(dispute => dispute.disputeType === disputeTypeFilter);
    }

    return filtered;
  };

  const filteredDisputes = getFilteredDisputes();

  // Stats cards data
  const statsCards = [
    {
      title: 'Total Disputes',
      value: stats.total,
      icon: <GavelIcon />,
      color: theme.palette.primary.main,
      bgColor: theme.palette.primary.light
    },
    {
      title: 'Open',
      value: stats.open,
      icon: <PendingIcon />,
      color: theme.palette.info.main,
      bgColor: theme.palette.info.light
    },
    {
      title: 'Under Review',
      value: stats.underReview,
      icon: <TrendingUpIcon />,
      color: theme.palette.warning.main,
      bgColor: theme.palette.warning.light
    },
    {
      title: 'Resolved',
      value: stats.resolved,
      icon: <ResolvedIcon />,
      color: theme.palette.success.main,
      bgColor: theme.palette.success.light
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Dispute Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review and manage user disputes
          </Typography>
        </Box>
        <Button
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
          variant="outlined"
        >
          Refresh
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      {stat.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      bgcolor: stat.bgColor,
                      color: stat.color,
                      p: 1.5,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {stat.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search disputes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All Statuses</MenuItem>
                {Object.values(DISPUTE_STATUS).map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Dispute Type</InputLabel>
              <Select
                value={disputeTypeFilter}
                label="Dispute Type"
                onChange={(e) => setDisputeTypeFilter(e.target.value)}
              >
                <MenuItem value="ALL">All Types</MenuItem>
                {Object.keys(DISPUTE_TYPE_LABELS).map((type) => (
                  <MenuItem key={type} value={type}>
                    {DISPUTE_TYPE_LABELS[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Dispute #</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Dispute Type</TableCell>
                <TableCell>Filer</TableCell>
                <TableCell>Respondent</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredDisputes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                    <Typography color="text.secondary">
                      No disputes found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredDisputes.map((dispute) => {
                  const statusConfig = getDisputeStatusConfig(dispute.status);
                  return (
                    <TableRow key={dispute.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {formatDisputeNumber(dispute.disputeNumber)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ maxWidth: 250 }}>
                          {dispute.title}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={DISPUTE_TYPE_LABELS[dispute.disputeType]}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{dispute.filerName}</TableCell>
                      <TableCell>{dispute.respondentName}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusConfig.label}
                          color={statusConfig.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(dispute.createdAt)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Review Dispute">
                          <IconButton
                            size="small"
                            onClick={() => handleReviewDispute(dispute)}
                            color="primary"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalElements}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Review Modal */}
      <DisputeReviewModal
        open={reviewModalOpen}
        onClose={() => setReviewModalOpen(false)}
        dispute={selectedDispute}
        onSuccess={handleReviewSuccess}
      />
    </Box>
  );
};

export default AdminDisputeDashboard;
